import React from 'react';
import RaisedButton from 'material-ui/RaisedButton';
import PropTypes from 'prop-types';
import Formsy from 'formsy-react';
import moment from 'moment';
import { Box } from '@material-ui/core';
import { VisitActualPropType } from '../../../propTypes/entities/visitActual';
import { actionTypes, outcomeCodesEndpoint } from '../../../settings/constants';
import globals from '../../../globals/communityGlobals';
import { getDateForApi, isValidDate } from '../../../utils/dates';
import { doubleActionPrevent } from '../../../utils/helpers';
import VisitActualActivitiesTable from './VisitActualActivitiesTable';
import ApiSelector from '../../../containers/common/selectors/selectorContainer';
import { ExternalProviderType } from '../../../enums';
import KeyboardTimeSelector from '../../common/KeyboardDateTimeSelectors/KeyboardTimeSelector';
import KeyboardDateSelector from '../../common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import { getErrorProps, checkRequiredOnBlur, combineDateTime } from '../../../utils/keyboardSelectorFunctions';

const locale = globals.getArea('visitActuals');
const errors = globals.getArea('error');

class VisitActualForm extends React.Component {
  constructor(props) {
    super(props);
    const { visitActual } = this.props;
    this.state = {
      canSubmit: false,
      actualStartDate: visitActual && visitActual.actual.startAt ? new Date(visitActual.actual.startAt) : null,
      actualStartTime: visitActual && visitActual.actual.startAt ? new Date(visitActual.actual.startAt) : null,
      actualEndDate: visitActual && visitActual.actual.endAt ? new Date(visitActual.actual.endAt) : null,
      actualEndTime: visitActual && visitActual.actual.endAt ? new Date(visitActual.actual.endAt) : null,
      outcomeCode: visitActual.outcomeCode,
      validationErrors: {},
    };
  }

  setValidationErrors = newValidationErrors => {
    this.setState({
      validationErrors: newValidationErrors,
    });
  };

  checkVisitTimeValidity = visitTime => {
    const { validationErrors } = this.state;
    const errorMsg = errors.getMsg('cannotBeEarlier', [locale.getMsg('dateFrom')]);
    const isVisitTimeNegative = visitTime < 0;

    if (isVisitTimeNegative) {
      this.setValidationErrors({
        ...this.state.validationErrors,
        actualEndDate: errorMsg,
      });
    } else if (validationErrors.actualEndDate === errorMsg) {
      this.setValidationErrors({
        ...this.state.validationErrors,
        actualEndDate: null,
      });
    }

    return !isVisitTimeNegative;
  };

  handleDateChange = (fieldName, date) => {
    const { actualStartTime, actualEndTime } = this.state;

    const visitTime =
      fieldName === 'actualStartDate'
        ? this.calculateVisitTime(date && actualStartTime ? combineDateTime(date, actualStartTime) : null, this.endAt)
        : this.calculateVisitTime(this.startAt, date && actualEndTime ? combineDateTime(date, actualEndTime) : null);

    const errorMsg = !date ? errors.getMsg('requiredFieldValidationMessage') : null;
    const visitTimeValid = date ? this.checkVisitTimeValidity(visitTime) : true;

    this.setState(
      prevState => ({
        [fieldName]: date,
        validationErrors: visitTimeValid
          ? { ...prevState.validationErrors, [fieldName]: errorMsg }
          : prevState.validationErrors,
      }),
      () => {
        this.props.updateVisitTime(Math.max(0, visitTime));
        if (this.additionalValidation()) this.enableButton();
      },
    );
  };

  handleTimeChange = (fieldName, time) => {
    const { actualStartDate, actualEndDate } = this.state;

    const visitTime =
      fieldName === 'actualStartTime'
        ? this.calculateVisitTime(actualStartDate && time ? combineDateTime(actualStartDate, time) : null, this.endAt)
        : this.calculateVisitTime(this.startAt, actualEndDate && time ? combineDateTime(actualEndDate, time) : null);

    const errorMsg = !time ? errors.getMsg('requiredFieldValidationMessage') : null;
    this.checkVisitTimeValidity(visitTime);

    this.setState(
      prevState => ({
        [fieldName]: time,
        validationErrors: { ...prevState.validationErrors, [fieldName]: errorMsg },
      }),
      () => {
        this.props.updateVisitTime(Math.max(0, visitTime));
        if (this.additionalValidation()) this.enableButton();
      },
    );
  };

  handleOutcomeCodeChange = (event, key, id, value) => {
    this.setState({
      ...this.state,
      outcomeCode: value.name,
    });
  };

  addCurrentValue = value => options => {
    if (value === undefined || value === null) {
      return options;
    }
    if (options) {
      if (!options.find(x => x.name === value)) {
        options.push({ id: -1, name: value });
      }
    }
    return options;
  };

  get startAt() {
    const { actualStartDate, actualStartTime } = this.state;
    return actualStartDate && actualStartTime ? combineDateTime(actualStartDate, actualStartTime) : null;
  }

  get endAt() {
    const { actualEndDate, actualEndTime } = this.state;
    return actualEndDate && actualEndTime ? combineDateTime(actualEndDate, actualEndTime) : null;
  }

  additionalValidation = () =>
    this.startAt &&
    this.endAt &&
    moment(this.endAt).isAfter(moment(this.startAt)) &&
    moment().isAfter(moment(this.endAt));

  enableButton = () => {
    this.setState({ canSubmit: true });
  };

  disableButton = () => {
    this.setState({ canSubmit: false });
  };

  formatDate = date => moment(date).format(globals.dateFormats.shortWithTime);

  save = doubleActionPrevent.bind(this)(
    'Save',
    // eslint-disable-next-line consistent-return
    async model => {
      if (!this.additionalValidation()) return false;

      const { submitAction, visitActualId } = this.props;

      const updated = {
        treatmentInstanceId: visitActualId,
        activities: model.activities,
        actualTime: {
          startAt: getDateForApi(this.startAt),
          endAt: getDateForApi(this.endAt),
        },
        outcomeCode: this.state.outcomeCode,
      };

      await submitAction(updated);
    },
    async () => {
      const { navigate } = this.props;
      navigate();
    },
  );

  calculateVisitTime = (startAt, endAt) => {
    return isValidDate(startAt, false) && isValidDate(endAt, false)
      ? Math.ceil(moment.duration(moment(endAt).diff(startAt)).asMinutes())
      : 0;
  };

  render() {
    const {
      actionType,
      visitActual,
      addActivityTime,
      visitActualId,
      enableIntegrationMode,
      externalProviderType,
    } = this.props;
    const {
      outcomeCode,
      actualStartDate,
      actualEndDate,
      actualStartTime,
      actualEndTime,
      validationErrors,
    } = this.state;

    const canEdit = visitActual.isMasterViewing;
    const isCreate = actionType === actionTypes.CREATE;
    const masterClinician = visitActual.clinicians.filter(c => c.isMaster)[0];

    return (
      <div className="editVisitActualsTable">
        <Formsy.Form
          onValidSubmit={this.submit}
          onValid={this.enableButton}
          onInvalid={this.disableButton}
          onSubmit={this.save}
        >
          {/* {visitActual.canNotBeSentToExternalProvider && (
            <div className="row">
              <span style={{ color: 'red' }}>{locale.getMsg('canNotSentCompleteVisitUpdateForExternalProvider')}</span>
              <br />
            </div>
          )} */}

          <div className="row" style={{ flexDirection: 'column' }}>
            <h5 type="title">
              {locale.getMsg('cliniciansPresent')}: {masterClinician.name}
            </h5>

            {visitActual.clinicians.filter(c => !c.isMaster).length > 0 && (
              <h5 type="title">
                {locale.getMsg('copies')}:{' '}
                {visitActual.clinicians
                  .filter(c => !c.isMaster)
                  .map(c => c.name)
                  .join(', ')}
              </h5>
            )}
            <br />
          </div>

          <div className="row">
            <h5 type="title">{locale.getMsg('visitActualDetails')}</h5>
            <Box className="actuals-form-wrap" display="flex" flexDirection="row" py={1} style={{ marginTop: '0.5%' }}>
              <Box
                className="col-c4"
                px={1}
                display="flex"
                flexDirection="column"
                style={{ width: '15%', gap: '10px' }}
              >
                <KeyboardDateSelector
                  name="plannedStartDate"
                  className="date-picker big-height"
                  label={locale.getMsg('plannedStartDate')}
                  value={visitActual.planned.startAt ? new Date(visitActual.planned.startAt) : null}
                  disabled
                />
                <KeyboardTimeSelector
                  name="plannedStartTime"
                  className="date-picker big-height"
                  label={locale.getMsg('plannedStartTime')}
                  value={visitActual.planned.startAt ? new Date(visitActual.planned.startAt) : null}
                  disabled
                />
              </Box>
              <Box
                className="col-c4"
                px={1}
                display="flex"
                flexDirection="column"
                style={{ width: '15%', gap: '10px' }}
              >
                <KeyboardDateSelector
                  name="actualStartDate"
                  className="date-picker big-height"
                  required
                  label={locale.getMsg('actualStartDate')}
                  value={actualStartDate}
                  disabled={!canEdit || visitActual.isSuccessfullyCompleted}
                  onChange={date => this.handleDateChange('actualStartDate', date)}
                  onBlur={event =>
                    checkRequiredOnBlur(
                      event.target.value,
                      this.setValidationErrors,
                      'actualStartDate',
                      validationErrors,
                    )
                  }
                  errorProps={getErrorProps(validationErrors ? validationErrors.actualStartDate : null)}
                  maxDate={new Date(globals.now)}
                  maxDateMessage={errors.getMsg('cannotBeInTheFuture')}
                />
                <KeyboardTimeSelector
                  name="actualStartTime"
                  className="date-picker big-height"
                  required
                  label={locale.getMsg('actualStartTime')}
                  value={actualStartTime}
                  disabled={!canEdit || visitActual.isSuccessfullyCompleted}
                  onChange={time => this.handleTimeChange('actualStartTime', time)}
                  onBlur={event =>
                    checkRequiredOnBlur(
                      event.target.value,
                      this.setValidationErrors,
                      'actualStartTime',
                      validationErrors,
                    )
                  }
                  errorProps={getErrorProps(validationErrors ? validationErrors.actualStartTime : null)}
                />
              </Box>
              <Box
                className="col-c4"
                px={1}
                display="flex"
                flexDirection="column"
                style={{ width: '15%', gap: '10px' }}
              >
                <KeyboardDateSelector
                  name="plannedEndDate"
                  className="date-picker big-height"
                  label={locale.getMsg('plannedEndDate')}
                  value={visitActual.planned.endAt ? new Date(visitActual.planned.endAt) : null}
                  disabled
                />
                <KeyboardTimeSelector
                  name="plannedEndTime"
                  className="date-picker big-height"
                  label={locale.getMsg('plannedEndTime')}
                  value={visitActual.planned.endAt ? new Date(visitActual.planned.endAt) : null}
                  disabled
                />
              </Box>
              <Box
                className="col-c4"
                px={1}
                display="flex"
                flexDirection="column"
                style={{ width: '15%', gap: '10px' }}
              >
                <KeyboardDateSelector
                  name="actualEndDate"
                  className="date-picker big-height"
                  required
                  label={locale.getMsg('actualEndDate')}
                  value={actualEndDate}
                  disabled={!canEdit || visitActual.isSuccessfullyCompleted}
                  minDate={actualStartDate}
                  minDateMessage={errors.getMsg('cannotBeEarlier', [locale.getMsg('dateFrom')])}
                  maxDate={new Date(globals.now)}
                  maxDateMessage={errors.getMsg('cannotBeInTheFuture')}
                  onChange={date => this.handleDateChange('actualEndDate', date)}
                  onBlur={event =>
                    checkRequiredOnBlur(event.target.value, this.setValidationErrors, 'actualEndDate', validationErrors)
                  }
                  errorProps={getErrorProps(validationErrors ? validationErrors.actualEndDate : null)}
                />
                <KeyboardTimeSelector
                  name="actualEndTime"
                  className="date-picker big-height"
                  required
                  label={locale.getMsg('actualEndTime')}
                  value={actualEndTime}
                  disabled={!canEdit || visitActual.isSuccessfullyCompleted}
                  onChange={time => this.handleTimeChange('actualEndTime', time)}
                  onBlur={event =>
                    checkRequiredOnBlur(event.target.value, this.setValidationErrors, 'actualEndTime', validationErrors)
                  }
                  errorProps={getErrorProps(validationErrors ? validationErrors.actualEndTime : null)}
                />
              </Box>
            </Box>
          </div>

          {enableIntegrationMode && externalProviderType === ExternalProviderType.Rio && (
            <div className="row">
              <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
                <ApiSelector
                  name="outcomeCode"
                  endpoint={outcomeCodesEndpoint}
                  value={outcomeCode}
                  onChange={this.handleOutcomeCodeChange}
                  reduxIdentifier="outcomeCodes"
                  className="select-field"
                  floatingLabelText={`${locale.getMsg('outcomeCode')}`}
                  isFetchRequired
                  multiple={false}
                  mapWithoutId
                  mapOptions={this.addCurrentValue(outcomeCode)}
                />
              </Box>
            </div>
          )}

          <div className="row">
            <h5 type="title">{locale.getMsg('visitActivities')}</h5>
            <VisitActualActivitiesTable
              name="activities"
              maxBand={visitActual.postingGradeEquivalence}
              treatmentInstanceId={Number(visitActualId)}
              value={visitActual.activities}
              visitTime={Math.max(this.calculateVisitTime(this.startAt, this.endAt), 0)}
              addActivityTime={addActivityTime}
              canEdit={canEdit}
              isSuccessfullyCompleted={visitActual.isSuccessfullyCompleted}
            />
          </div>

          {canEdit && (
            <div className="row">
              <div className="col-c4">
                <RaisedButton
                  primary
                  label={locale.getMsg(`${isCreate ? 'create' : 'edit'}SubmitLabel`)}
                  type="submit"
                  disabled={
                    !this.state.canSubmit || !this.additionalValidation() || visitActual.isSuccessfullyCompleted
                  }
                />
              </div>
            </div>
          )}
        </Formsy.Form>
      </div>
    );
  }
}

VisitActualForm.defaultProps = {
  visitActual: null,
};

VisitActualForm.propTypes = {
  visitActualId: PropTypes.string.isRequired,
  visitActual: VisitActualPropType,
  actionType: PropTypes.string.isRequired,
  submitAction: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  updateVisitTime: PropTypes.func.isRequired,
  addActivityTime: PropTypes.func.isRequired,
};

export default VisitActualForm;
